import { createPinia } from "pinia";
import { createApp } from "vue";

import App from "./App.vue";
import i18n from "./i18n";
import router, { installAuthorizationRouting } from "./router";
import { useAuthStore } from "@/stores/auth";
import { useConsentStore } from "@/stores/consent";
import { installCloudflareBeacon } from "@/lib/analytics/cloudflareBeacon";
import { configureSentry } from "@/lib/observability/sentry";
import "./style.css";

// Renders a fatal bootstrap error in place of the app instead of leaving
// a blank page. Used when the app cannot safely start — e.g. a
// production build with no Firebase config (AuthConfigError), where
// silently mounting would mean broken auth with no visible cause.
function renderFatalError(): void {
  const el = document.querySelector("#app");
  if (!el) return;
  el.innerHTML = `
    <main style="max-width:32rem;margin:4rem auto;padding:0 1.5rem;font-family:system-ui,sans-serif;line-height:1.5;">
      <h1 style="font-size:1.25rem;">RevisionLab is temporarily unavailable</h1>
      <p>The app is misconfigured and can't start safely. Our team has been
      notified. Please try again shortly.</p>
    </main>`;
}

// Wrapped in an async IIFE rather than top-level await because Vite's
// default esbuild target (es2020) predates top-level await (ES2022).
// Bumping the target would break on older browsers we want to support;
// the IIFE achieves the same ordering with no compatibility cost.
(async () => {
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(i18n);
  app.use(router);

  // tutor-70k.2 — Sentry must initialise BEFORE the router is exercised
  // so the browserTracingIntegration can hook navigation breadcrumbs.
  // No-op when VITE_SENTRY_DSN is unset. Initialised first so it can
  // also capture a fatal bootstrap error below.
  configureSentry(app, router);

  try {
    // Wait for the auth client to determine initial session state before
    // mounting. For Firebase this awaits IndexedDB restoration
    // (~50–200ms on cold reload); for the mock this resolves immediately.
    // Without this, reloads of authenticated users briefly flash the
    // unauthenticated UI before `onIdTokenChanged` restores the session,
    // and any API call fired during that gap 401s.
    //
    // useAuthStore() also throws AuthConfigError if this is a production
    // build with no Firebase config — caught below so it surfaces as a
    // visible error + Sentry report rather than fake (mock) auth.
    const auth = useAuthStore();
    await auth.ready;

    // Claims can change after the initial navigation, and entitlement
    // revocation is only knowable when the server refuses the next request.
    // Keep the current route aligned with both signals instead of waiting for
    // another click or leaving every request on the page to fail again.
    installAuthorizationRouting();

    // tutor-70k.5 + A25 — Statsig comes up only once the consent store has
    // resolved consent for the identity on screen (a live consent row for an
    // account, an "accepted" answer on this device for a visitor without
    // one), so this file does not call `configureStatsig` at all:
    // `setAnalyticsConsent` is the only door in, and only the consent store
    // opens it. Deliberately NOT awaited — the consent fetch must not block
    // mount, and the pre-fetch state is "no analytics", which is the safe
    // state to render in.
    const consent = useConsentStore();
    void consent.refresh();

    // Re-resolve consent only when the account changes. Token refreshes
    // also mutate the auth store; subscribing to every mutation would make
    // a stale-token 401 recurse through consent.refresh(). The running client
    // is never re-identified: `refresh` sees the new identity, shuts the
    // client down before it yields, and starts one for the new identity
    // only once that identity's own consent is known.
    auth.onIdentityChanged(() => {
      void consent.refresh();
    });

    app.mount("#app");

    // DPIA P12 — page statistics for the marketing page this visit landed
    // on, decided once the first navigation (and any redirect) has settled.
    void router.isReady().then(() => {
      installCloudflareBeacon({
        path: router.currentRoute.value.path,
        signedIn: auth.user !== null,
      });
    });
  } catch (err) {
    // Fatal: do not mount with broken auth. Surface visibly and re-throw
    // so Sentry's global handler captures it (configureSentry ran above).
    console.error("Fatal bootstrap error", err);
    renderFatalError();
    throw err;
  }
})();
